(() => {
  const DATE_COUNT = 14;
  const INITIAL_DATE_COUNT = 5;

  const localDateValue = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const setControlValue = (control, value) => {
    if (!control) return;
    control.value = value;
    control.setAttribute('value', value);
  };

  const restoreDefaultValues = (planner) => {
    planner.querySelectorAll('[data-bf-default-value]').forEach((control) => {
      setControlValue(control, control.dataset.bfDefaultValue || '');
    });
  };

  const selectedBookingValues = (planner) => ({
    date: planner.querySelector('[data-date][aria-selected="true"]')?.dataset.date || '',
    time: planner.querySelector('[data-bf-time-button][aria-selected="true"]')?.dataset.time || '',
  });

  const clearBookingError = (planner) => {
    const error = planner.querySelector('[data-bf-booking-error]');
    if (error) error.hidden = true;
    planner.classList.remove('bf-purchase-planner--invalid');
  };

  const updateBookingSummary = (planner) => {
    const { date: selectedDate, time: selectedTime } = selectedBookingValues(planner);
    const summary = planner.querySelector('[data-bf-booking-summary]');
    if (!summary) return;

    if (!selectedDate || !selectedTime) {
      summary.hidden = true;
      return;
    }

    const [year, month, day] = selectedDate.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const label = new Intl.DateTimeFormat('de-DE', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }).format(date);
    summary.textContent = `Dein Terminwunsch: ${label} um ${selectedTime} Uhr`;
    summary.hidden = false;
  };

  const plannerForForm = (form) => {
    if (!form?.id) return null;
    return [...document.querySelectorAll('[data-bf-purchase-planner]')].find((item) =>
      item.querySelector('[data-bf-appointment-date]')?.getAttribute('form') === form.id
    );
  };

  const addPlannerProperties = (formData, planner) => {
    const appointmentCard = planner.querySelector('[data-bf-appointment-card]');
    const shippingCard = planner.querySelector('[data-bf-shipping-card]');

    if (!appointmentCard?.hidden) {
      const { date, time } = selectedBookingValues(planner);
      const vin = planner.querySelector('[name="properties[VIN letzte 7 Stellen]"]')?.value.trim() || '';
      formData.set('properties[Wunschtermin]', date);
      formData.set('properties[Uhrzeit]', time);
      formData.set('properties[Terminstatus]', 'Terminwunsch – Bestätigung ausstehend');
      if (vin) formData.set('properties[VIN letzte 7 Stellen]', vin);
    }

    if (!shippingCard?.hidden) {
      planner.querySelectorAll('[data-bf-shipping-control][data-bf-default-value]').forEach((control) => {
        formData.set(control.name, control.dataset.bfDefaultValue || '');
      });
    }
  };

  const initBookingPicker = (planner) => {
    if (planner.dataset.bfBookingReady === 'true') return;

    const datePicker = planner.querySelector('[data-bf-date-picker]');
    const dateInput = planner.querySelector('[data-bf-appointment-date]');
    const timeInput = planner.querySelector('[data-bf-appointment-time]');
    const timeStep = planner.querySelector('[data-bf-time-step]');
    const moreDatesButton = planner.querySelector('[data-bf-more-dates]');
    if (!datePicker || !dateInput || !timeInput || !timeStep || !moreDatesButton) return;

    const weekdayFormatter = new Intl.DateTimeFormat('de-DE', { weekday: 'short' });
    const monthFormatter = new Intl.DateTimeFormat('de-DE', { month: 'short' });
    const today = new Date();
    today.setHours(12, 0, 0, 0);

    for (let offset = 0; offset < DATE_COUNT; offset += 1) {
      const date = new Date(today);
      date.setDate(today.getDate() + offset);
      const value = localDateValue(date);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'bf-date-option';
      button.dataset.date = value;
      button.setAttribute('role', 'option');
      button.setAttribute('aria-selected', 'false');
      button.hidden = offset >= INITIAL_DATE_COUNT;
      button.setAttribute('aria-label', new Intl.DateTimeFormat('de-DE', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
      }).format(date));
      button.innerHTML = `
        <span class="bf-date-option__weekday">${offset === 0 ? 'Heute' : weekdayFormatter.format(date)}</span>
        <strong>${date.getDate()}</strong>
        <span class="bf-date-option__month">${monthFormatter.format(date)}</span>
      `;
      datePicker.append(button);
    }

    moreDatesButton.addEventListener('click', () => {
      datePicker.querySelectorAll('[data-date][hidden]').forEach((button) => {
        button.hidden = false;
      });
      moreDatesButton.setAttribute('aria-expanded', 'true');
      moreDatesButton.hidden = true;
      window.BimmerfitConversion?.publish('bimmerfit:more_dates_opened');
    });

    datePicker.addEventListener('click', (event) => {
      const button = event.target.closest('[data-date]');
      if (!button || button.disabled) return;

      datePicker.querySelectorAll('[data-date]').forEach((item) => {
        item.setAttribute('aria-selected', String(item === button));
      });
      event.preventDefault();
      event.stopPropagation();
      setControlValue(dateInput, button.dataset.date);
      setControlValue(timeInput, '');
      planner.querySelectorAll('[data-bf-time-button]').forEach((item) => item.setAttribute('aria-selected', 'false'));
      timeStep.hidden = false;
      clearBookingError(planner);
      updateBookingSummary(planner);
      window.BimmerfitConversion?.publish('bimmerfit:appointment_date_selected', {
        selected_date: button.dataset.date,
      });
    });

    planner.querySelectorAll('[data-bf-time-button]').forEach((button) => {
      button.addEventListener('click', (event) => {
        if (button.disabled) return;
        event.preventDefault();
        event.stopPropagation();
        planner.querySelectorAll('[data-bf-time-button]').forEach((item) => {
          item.setAttribute('aria-selected', String(item === button));
        });
        setControlValue(timeInput, button.dataset.time);
        clearBookingError(planner);
        updateBookingSummary(planner);
        window.BimmerfitConversion?.publish('bimmerfit:appointment_time_selected', {
          selected_time: button.dataset.time,
        });
      });
    });

    restoreDefaultValues(planner);
    planner.dataset.bfBookingReady = 'true';
  };

  const readModes = (planner) => {
    const source = planner.nextElementSibling;
    if (!source?.matches('[data-bf-variant-modes]')) return {};

    try {
      return JSON.parse(source.textContent);
    } catch (error) {
      console.warn('Bimmerfit: Variantenregeln konnten nicht gelesen werden.', error);
      return {};
    }
  };

  const selectedVariantId = () => {
    const selectedVariant = document.querySelector('input[type="radio"][data-variant-id]:checked');
    return selectedVariant?.dataset.variantId || new URLSearchParams(window.location.search).get('variant');
  };

  const syncPlanner = (planner, requestedVariantId) => {
    initBookingPicker(planner);
    const modes = readModes(planner);
    const variantId = requestedVariantId || selectedVariantId() || Object.keys(modes)[0];
    const mode = modes[variantId] || { shipping: false, appointment: true };
    const appointmentCard = planner.querySelector('[data-bf-appointment-card]');
    const shippingCard = planner.querySelector('[data-bf-shipping-card]');

    if (appointmentCard) appointmentCard.hidden = !mode.appointment;
    if (shippingCard) shippingCard.hidden = !mode.shipping;

    planner.querySelectorAll('[data-bf-appointment-control]').forEach((control) => {
      control.disabled = !mode.appointment;
    });
    planner.querySelectorAll('[data-date], [data-bf-time-button]').forEach((button) => {
      button.disabled = !mode.appointment;
    });
    if (!mode.appointment) clearBookingError(planner);
    planner.querySelectorAll('[data-bf-shipping-control]').forEach((control) => {
      control.disabled = !mode.shipping;
    });
    restoreDefaultValues(planner);
  };

  const syncAll = (variantId) => {
    document.querySelectorAll('[data-bf-purchase-planner]').forEach((planner) => syncPlanner(planner, variantId));
  };

  const syncAfterThemeUpdate = (variantId) => {
    syncAll(variantId);
    window.setTimeout(() => syncAll(variantId), 0);
    window.setTimeout(() => syncAll(), 500);
  };

  document.addEventListener('change', (event) => {
    if (event.target?.matches('[data-variant-id], select[name^="options"]')) syncAfterThemeUpdate();
  }, true);

  document.addEventListener('submit', (event) => {
    const planner = plannerForForm(event.target);
    const appointmentCard = planner?.querySelector('[data-bf-appointment-card]');
    if (!planner || appointmentCard?.hidden) return;

    const dateInput = planner.querySelector('[data-bf-appointment-date]');
    const timeInput = planner.querySelector('[data-bf-appointment-time]');
    const { date: selectedDate, time: selectedTime } = selectedBookingValues(planner);
    setControlValue(dateInput, selectedDate);
    setControlValue(timeInput, selectedTime);
    restoreDefaultValues(planner);
    if (selectedDate && selectedTime) {
      window.BimmerfitConversion?.publish('bimmerfit:appointment_submitted', {
        selected_date: selectedDate,
        selected_time: selectedTime,
      });
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    window.BimmerfitConversion?.publish('bimmerfit:appointment_validation_error', {
      missing_date: !selectedDate,
      missing_time: !selectedTime,
    });
    const error = planner.querySelector('[data-bf-booking-error]');
    if (error) error.hidden = false;
    planner.classList.add('bf-purchase-planner--invalid');
    const focusTarget = selectedDate
      ? planner.querySelector('[data-bf-time-button]')
      : planner.querySelector('[data-date]');
    focusTarget?.focus();
    planner.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, true);

  // Horizon baut beim Variantenwechsel Teile des Produktformulars neu auf und
  // kann dabei die Werte externer form-gebundener Inputs zurücksetzen. Das
  // formdata-Ereignis läuft direkt beim Erstellen der Warenkorb-Anfrage und
  // stellt sicher, dass genau die sichtbare Auswahl übertragen wird.
  document.addEventListener('formdata', (event) => {
    const planner = plannerForForm(event.target);
    if (planner) addPlannerProperties(event.formData, planner);
  }, true);

  document.addEventListener('shopify:product:select', (event) => {
    syncAfterThemeUpdate(event.detail?.detail?.variantId || event.detail?.variantId);
  });

  new MutationObserver((changes) => {
    if (changes.some((change) => [...change.addedNodes].some((node) =>
      node.nodeType === Node.ELEMENT_NODE && (node.matches?.('[data-bf-purchase-planner]') || node.querySelector?.('[data-bf-purchase-planner]'))
    ))) syncAll();
  }).observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => syncAll(), { once: true });
  } else {
    syncAll();
  }
})();
